import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Users2, PowerOff, Power } from "lucide-react";

export default function Employees() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setItems(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id, name) => {
    if (!window.confirm(`${name} personelini VE tüm puantaj/avans kayıtlarını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error("Silinemedi: " + (e?.response?.data?.detail || e?.message || ""));
    }
  };

  const toggleActive = async (emp) => {
    try {
      await api.put(`/employees/${emp.id}`, { active: !emp.active });
      toast.success(!emp.active ? "Aktif edildi" : "Pasif edildi");
      load();
    } catch (e) { toast.error("Güncellenemedi"); }
  };

  const visible = items.filter((e) => showInactive || e.active);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Personel Yönetimi</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Personel</h1>
          <p className="text-xs text-neutral-500 mt-1.5 max-w-xl">
            Personel kayıtları, aylık puantaj (mesai takibi) ve verilen avanslar burada tutulur.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1.5 cursor-pointer select-none">
            <input type="checkbox" data-testid="employees-show-inactive" checked={showInactive}
                   onChange={(e) => setShowInactive(e.target.checked)}
                   className="w-4 h-4 accent-neutral-600" />
            Pasifleri göster
          </label>
          <button
            data-testid="employee-add"
            onClick={() => { setEditing(null); setOpen(true); }}
            className="brutal-btn bg-black text-white px-4 py-2.5 text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} /> Yeni Personel
          </button>
        </div>
      </header>

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="text-left px-4 py-3 label-caps">Ad Soyad</th>
              <th className="text-left px-4 py-3 label-caps">Pozisyon</th>
              <th className="text-left px-4 py-3 label-caps">Telefon</th>
              <th className="text-left px-4 py-3 label-caps">İşe Başlama</th>
              <th className="text-right px-4 py-3 label-caps">Aylık Maaş</th>
              <th className="text-center px-4 py-3 label-caps">Durum</th>
              <th className="px-4 py-3 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-neutral-500">Yükleniyor…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-neutral-500 text-sm">
                Kayıt yok. <b>Yeni Personel</b> ile başlayın.
              </td></tr>
            ) : visible.map((e) => (
              <tr key={e.id} className={`border-b border-black/5 hover:bg-neutral-50 ${!e.active ? "opacity-50" : ""}`}>
                <td className="px-4 py-3 font-medium">
                  <Link to={`/personel/${e.id}`} data-testid={`employee-name-${e.id}`}
                        className="hover:underline decoration-dotted underline-offset-4 inline-flex items-center gap-2">
                    <Users2 size={14} className="text-neutral-400" />
                    {e.name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-neutral-700">{e.position || "—"}</td>
                <td className="px-4 py-3 font-mono-num text-neutral-700">{e.phone || "—"}</td>
                <td className="px-4 py-3 font-mono-num text-neutral-700">{e.start_date ? fmtDate(e.start_date) : "—"}</td>
                <td className="px-4 py-3 text-right font-mono-num">{e.monthly_salary ? fmtTRY(e.monthly_salary) : "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest border ${e.active ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-neutral-400 bg-neutral-100 text-neutral-600"}`}>
                    {e.active ? "Aktif" : "Pasif"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => toggleActive(e)} className="p-1.5 hover:bg-neutral-100 mr-0.5"
                          title={e.active ? "Pasif Yap" : "Aktif Yap"}
                          data-testid={`employee-toggle-${e.id}`}>
                    {e.active ? <PowerOff size={14} /> : <Power size={14} className="text-emerald-700" />}
                  </button>
                  <button onClick={() => { setEditing(e); setOpen(true); }} className="p-1.5 hover:bg-neutral-100"
                          data-testid={`employee-edit-${e.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(e.id, e.name)} className="p-1.5 hover:bg-rose-50"
                          data-testid={`employee-delete-${e.id}`}>
                    <Trash2 size={14} className="text-rose-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <EmployeeModal editing={editing} onClose={() => setOpen(false)}
                       onSaved={() => { setOpen(false); load(); }} />
      )}
    </div>
  );
}

function EmployeeModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    name: "", tc: "", phone: "", position: "", start_date: "", monthly_salary: "", notes: "", active: true,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Ad Soyad zorunlu"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      payload.monthly_salary = form.monthly_salary === "" ? null : Number(form.monthly_salary);
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      onSaved();
    } catch (err) {
      toast.error("Kaydedilemedi: " + (err?.response?.data?.detail || err?.message || ""));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            data-testid="employee-modal"
            className="bg-white border border-black w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Personeli Düzenle" : "Yeni Personel"}</h2>
          <button type="button" onClick={onClose} className="p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <F label="Ad Soyad *">
            <input required data-testid="employee-name" value={form.name || ""}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="TC Kimlik No">
              <input data-testid="employee-tc" value={form.tc || ""} maxLength={11}
                     onChange={(e) => setForm({ ...form, tc: e.target.value.replace(/\D/g, "") })}
                     className="input font-mono-num" />
            </F>
            <F label="Telefon">
              <input data-testid="employee-phone" value={form.phone || ""}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     className="input font-mono-num" placeholder="0555 000 00 00" />
            </F>
          </div>
          <F label="Pozisyon">
            <input data-testid="employee-position" value={form.position || ""}
                   onChange={(e) => setForm({ ...form, position: e.target.value })}
                   className="input" placeholder="Operatör / Muhasebe / Sürücü" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="İşe Başlama Tarihi">
              <input type="date" data-testid="employee-start-date" value={form.start_date || ""}
                     onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input font-mono-num" />
            </F>
            <F label="Aylık Maaş (₺)">
              <input type="number" step="0.01" data-testid="employee-salary" value={form.monthly_salary ?? ""}
                     onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })}
                     className="input font-mono-num" />
            </F>
          </div>
          <F label="Not">
            <textarea rows={2} data-testid="employee-notes" value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
          </F>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button type="submit" disabled={saving} data-testid="employee-save"
                  className="brutal-btn bg-black text-white px-5 py-2 text-sm font-medium disabled:opacity-40">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.55rem 0.75rem;outline:none;font-size:0.9rem}
                  .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1.5">{label}</div>
      {children}
    </label>
  );
}
